import { useState, useRef, useEffect } from 'react';
// Use the full echarts bundle so all chart types and components are available.
// Tree-shaking sub-paths (echarts/core, echarts/charts, etc.) are not supported
// by all echarts versions / bundler configurations.
import * as echarts from 'echarts';

interface EChartsViewProps {
  /** A complete ECharts option object from the ERAX JSON response. */
  option: Record<string, unknown>;
  /** Chart height in pixels. Default `400`. */
  height?: number;
}

/**
 * Renders an ECharts chart from a raw ECharts option configuration.
 * Used when ERAX returns pre-built chart specs via `:::ECHARTS_JSON_START:::`.
 */
export function EChartsView({ option, height = 400 }: EChartsViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize and update chart
  useEffect(() => {
    if (!containerRef.current) return;

    // Dispose previous instance if it exists
    if (chartRef.current) {
      chartRef.current.dispose();
      chartRef.current = null;
    }

    setError(null);

    try {
      const chart = echarts.init(containerRef.current);
      chartRef.current = chart;
      chart.setOption(option as echarts.EChartsOption);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error rendering chart';
      console.error('[EChartsView] Failed to render chart:', msg);
      setError(msg);
      return;
    }

    // Resize observer for responsive behavior
    const container = containerRef.current;
    const resizeObserver = new ResizeObserver(() => {
      chartRef.current?.resize();
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [option]);

  if (error) {
    return (
      <div
        className="flex items-center justify-center rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs p-4"
        style={{ height }}
      >
        Chart rendering failed: {error}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height }}
    />
  );
}
